import { ConnectedProps } from "react-redux";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import BackButton from "../BackButton";
import TabletPageDefault, { TabletContainer } from "../Layout/TabletLayout";
import { MainColumn, RightRail, TwoColumnGrid } from "../Layout/TwoColumn";
import { connector } from "./redux";
import { showTranslated, translate } from "../../../config/translator";
import MatchStatusDisplay from "./MatchStatusDisplay";
import {
  getCardEvents,
  getChangeEvents,
  getGoalEvents,
  getMatchDate,
  MATCH_STATUS,
  matchLengthMinutesRaw,
  matchStatusDate,
  sortEventsMinute
} from "../../../config/utils";
import { ClavaContext, ClavaRootContext } from "../../../config/contexts";
import MatchScoreDisplay from "./MatchScoreDisplay";
import ScaleFade from "../Animated/ScaleFade";
import RoundedRectangle from "../RoundedRectangle";
import { AdPositionEnum, EventType, EventTypeEnum, MatchScreen, SportEnum } from "../../../client/api";
import { pathMatchView, pathTeamView, type View } from "../../../config/routes";
import MatchEvent from "./MatchEvent";
import Lineup from "../Lineup";
import MatchChanges from "./MatchChanges";
import RightPanel from "./RightPanel";
import MatchDayPanel from "./RightPanel/MatchDayPanel";
import Table from "../Table";
import StoreBanner from "../StoreBanner";
import SegmentedTabs from "../SegmentedTabs";
import { AdSlot } from "../AdSlot";
import FutureMatchIcon from "../../../assets/svg/FutureMatchIcon";
import { AdSensePositionEnum } from "../../../config/adsenseConfig";
import Loading from "../Loading";
import ClavaImage from "../ClavaImage";

const MatchWrapper: React.FC<
  ConnectedProps<typeof connector> & { currentView?: View }
> = ({
       matchId,
       currentView,
       screenFromStore,
       loadMatchScreen
     }) => {
  useEffect(() => {
    if (!screenFromStore) {
      loadMatchScreen(matchId);
    }
  }, [loadMatchScreen, matchId, screenFromStore]);
  if (!screenFromStore)
    return <Loading />;
  return <Match screen={screenFromStore} currentView={currentView} />;
};
const Match: React.FC<
  { screen: MatchScreen, currentView?: View }
> = ({
       screen,
       currentView
     }) => {
  const startTime = useMemo(() => getMatchDate(screen.leagueMatch).getTime(), [screen.leagueMatch]);
  const endTime = useMemo(() => new Date(screen.leagueMatch.endTime).getTime(), [screen.leagueMatch.endTime]);
  const matchLength = useMemo(() => matchLengthMinutesRaw(screen.leagueMatch.league.matchSectionAmount, screen.leagueMatch.league.matchSectionDurationMinutes), [screen.leagueMatch.league.matchSectionAmount, screen.leagueMatch.league.matchSectionDurationMinutes]);
  const [status, setStatus] = useState<MATCH_STATUS>(
    matchStatusDate(
      startTime,
      matchLength,
      endTime));
  const { l } = useContext(ClavaContext);
  const { theme } = useContext(ClavaRootContext);
  const view = currentView;


  useEffect(() => {
    const interval = window.setInterval(() => {
      setStatus(
        matchStatusDate(
          startTime,
          matchLength,
          endTime)
      );
    }, 5000);

    return () => {
      clearInterval(interval);

    };
  }, [endTime, startTime, matchLength]);

  const filteredEvents = useMemo(() => screen.leagueMatch.events
    .filter(
      (e): e is EventType =>
        e.type === EventTypeEnum.GOAL ||
        e.type === EventTypeEnum.CHANCE ||
        e.type === EventTypeEnum.CARD
    )
    .sort(sortEventsMinute), [screen.leagueMatch.events]);
  const live = typeof status === "number";


  return (
    <TabletPageDefault>
      <TabletContainer>
        <TwoColumnGrid className="xl:max-w-6xl">
          <MainColumn>
            <RoundedRectangle
              className="w-full pt-12 pb-4 bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark rounded-xl flex-shrink-0 relative"
              hasBorder
              hasTitle={false}
            >
              <BackButton />
              <div className="grid grid-cols-3 items-start justify-items-center w-full h-full">
                <div className="flex flex-col items-center justify-start px-2">
                  <NavLink
                    to={pathTeamView(screen.leagueMatch.team1.id, "table")}
                    className="cursor-pointer"
                  >
                    <ScaleFade isOpen hover>
                      <div className="w-12 h-12 flex items-center justify-center bg-transparent">
                        <ClavaImage
                          image={screen.leagueMatch.team1.emblem}
                          alt={showTranslated(screen.leagueMatch.team1.name, l)}
                          width="100%"
                        />
                      </div>
                    </ScaleFade>
                  </NavLink>
                  <NavLink
                    to={pathTeamView(screen.leagueMatch.team1.id, "table")}
                    className="cursor-pointer font-medium text-font dark:text-font-dark text-sm text-center mt-2"
                  >
                    {showTranslated(screen.leagueMatch.team1.name, l)}
                  </NavLink>
                </div>
                <div className="flex flex-col items-center justify-center h-full">
                  {screen.leagueMatch.cancelled ? (
                    <span className="text-red">
                    {translate("cancelledFull", l)}
                  </span>
                  ) : (
                    <>
                      {status !== "future" && (
                        <MatchScoreDisplay
                          goal1={screen.leagueMatch.goal1}
                          className={`text-center font-semibold ${
                            live ? "text-red" : "text-font dark:text-font-dark"
                          } text-base`}
                          goal2={screen.leagueMatch.goal2}
                        />
                      )}
                      <MatchStatusDisplay
                        startDate={startTime}
                        endTime={endTime}
                        fullDate
                        sectionAmount={screen.leagueMatch.league.matchSectionAmount}
                        sectionDuration={screen.leagueMatch.league.matchSectionDurationMinutes}
                        pauseDuration={screen.leagueMatch.league.matchSectionPauseDurationMinutes}
                        hideLive={screen.leagueMatch.league.sports === SportEnum.SOCCER}
                        hideMinute={screen.leagueMatch.league.sports === SportEnum.HOCKEY}
                        statusOverride={status}
                        mode="matchpage"
                      />
                    </>
                  )}
                </div>
                <div className="flex flex-col items-center justify-start px-2">
                  <NavLink
                    to={pathTeamView(screen.leagueMatch.team2.id, "table")}
                    className="cursor-pointer"
                  >
                    <ScaleFade isOpen hover>
                      <div className="w-12 h-12 flex items-center justify-center bg-transparent">
                        <ClavaImage
                          image={screen.leagueMatch.team2.emblem}
                          alt={showTranslated(screen.leagueMatch.team2.name, l)}
                          width="100%"
                        />
                      </div>
                    </ScaleFade>
                  </NavLink>
                  <NavLink
                    to={pathTeamView(screen.leagueMatch.team2.id, "table")}
                    className="cursor-pointer font-medium text-font dark:text-font-dark text-sm text-center mt-2"
                  >
                    {showTranslated(screen.leagueMatch.team2.name, l)}
                  </NavLink>
                </div>
              </div>
            </RoundedRectangle>
            <div className="mx-auto w-full xl:mx-0">
              <div className="mt-6 block xl:hidden">
                <RightPanel
                  player1={screen.topScorer.topScorer1}
                  player2={screen.topScorer.topScorer2}
                  emblem1={screen.leagueMatch.team1.emblem}
                  emblem2={screen.leagueMatch.team2.emblem}
                  goals1={screen.topScorer.topScorer1Goals}
                  goals2={screen.topScorer.topScorer2Goals}
                />
              </div>
              <SegmentedTabs
                className="justify-start w-full pt-3 pb-6 mt-3"
                activeKey={view ?? "highlights"}
                replace
                items={[
                  {
                    key: "highlights",
                    label: translate("highlights", l),
                    to: pathMatchView(screen.leagueMatch.id, "highlights")
                  },
                  {
                    key: "lineup",
                    label: translate("lineup", l),
                    to: pathMatchView(screen.leagueMatch.id, "lineup"),
                    hidden: !screen.leagueMatch.league.hasLineup
                  },
                  {
                    key: "table",
                    label: translate("table", l),
                    to: pathMatchView(screen.leagueMatch.id, "table")
                  },
                  {
                    key: "statistics",
                    label: translate("statistics", l),
                    to: pathMatchView(screen.leagueMatch.id, "statistics")
                  }
                ]}
              />
              {(!view || view === "highlights") && (
                <RoundedRectangle
                  className="rounded-xl bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark mb-4  w-full"
                  hasBorder
                  hasTitle
                  titleType="upCenter"
                  titleKey="highlights"
                >
                  <div
                    className="relative flex flex-col items-stretch justify-start mb-4 pt-8 px-8 xl:pt-6 xl:px-6">
                    {filteredEvents.map((event, index) => (
                      <MatchEvent
                        event={event}
                        team1Id={screen.leagueMatch.team1.id}
                        key={`match-event-${event.id}`}
                        allEvents={filteredEvents}
                        currentEventIndex={index}
                        sports={screen.leagueMatch.league.sports}
                      />
                    ))}
                    {status === "future" && (
                      <div className="flex flex-col items-center justify-center gap-6 py-8">
                        <FutureMatchIcon
                          className="w-40 h-40 max-w-full"
                          variant={theme === "dark" ? "dark" : "light"}
                          aria-hidden
                        />
                        <span className="text-center text-font-600 dark:text-font-600-dark">
                              {translate("matchNotStarted", l)}
                            </span>
                      </div>
                    )}
                    {filteredEvents.length === 0 && status !== "future" && (
                      <span
                        className="w-full h-24 flex items-center justify-center text-center text-font-600 dark:text-font-600-dark">
                            {translate("noEventsEntered", l)}
                          </span>
                    )}
                    {filteredEvents.length > 0 && status !== "future" && (
                      <RoundedRectangle
                        className="mx-auto mt-2 flex flex-col items-center justify-between rounded px-4 py-2 bg-primary-50 dark:bg-primary-50-dark w-full flex-shrink-0"
                        hasTitle={false}
                        hasBorder={false}
                      >
                          <span className="font-medium text-center text-primary text-xs">
                            {translate("highlightsScoreTitle", l)}
                          </span>
                        <span className="font-semibold text-font dark:text-font-dark text-center text-sm">
                            {screen.leagueMatch.goal1} - {screen.leagueMatch.goal2}
                          </span>
                      </RoundedRectangle>
                    )}
                  </div>
                </RoundedRectangle>
              )}
              {screen.leagueMatch.league.hasLineup && (
                <div className={view === "lineup" ? "" : "hidden"}>
                  <RoundedRectangle
                    className="rounded-xl bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark mb-4  w-full"
                    hasBorder
                    hasTitle
                    titleType="upCenter"
                    titleKey="lineup"
                  >
                    <Lineup
                      matchId={screen.leagueMatch.id}
                      team1Id={screen.leagueMatch.team1.id}
                      team2Id={screen.leagueMatch.team2.id}
                      team1={screen.leagueMatch.team1}
                      team2={screen.leagueMatch.team2}
                      lineup1={screen.leagueMatch.lineupTeam1 ?? undefined}
                      lineup2={screen.leagueMatch.lineupTeam2 ?? undefined}
                      cardEvents={getCardEvents(screen.leagueMatch.events)}
                      changeEvents={getChangeEvents(screen.leagueMatch.events)}
                      goalEvents={getGoalEvents(screen.leagueMatch.events)}
                      playerRatings={screen.playerRating}
                    />
                  </RoundedRectangle>
                </div>
              )}
              {screen.leagueMatch.league.hasLineup && view === "lineup" && (
                <div className="mb-4">
                  <AdSlot
                    type={AdPositionEnum.MATCH_LINEUP}
                    className="w-full"
                  />
                </div>
              )}
              <div className={view === "table" ? "" : "hidden"}>
                <div className="rounded-xl mb-4  w-full">
                  <Table
                    team2Id={screen.leagueMatch.team2.id}
                    team1Id={screen.leagueMatch.team1.id}
                    leagueId={screen.leagueMatch.league.id}
                  />
                </div>
              </div>
              <div className={view === "statistics" ? "" : "hidden"}>
                <RoundedRectangle
                  className="rounded-xl bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark mb-4  w-full"
                  hasBorder
                  hasTitle
                  titleType="upCenter"
                  titleKey="statistics"
                >
                  <StoreBanner border={false} message={translate("downloadUnlimited", l)} />
                </RoundedRectangle>
              </div>
              {status !== "future" && (!view || view === "highlights" || view === "lineup") && (
                <div className="w-full">
                  <MatchChanges
                    events={getChangeEvents(screen.leagueMatch.events)}
                    team1Id={screen.leagueMatch.team1.id}
                    team2Id={screen.leagueMatch.team2.id}
                  />
                </div>
              )}
              <div className="mb-4">
                <AdSlot
                  type={AdPositionEnum.MATCH_SCREEN_BANNER}
                  className="w-full"
                />
              </div>
              <div className="mt-6 xl:hidden">
                <MatchDayPanel
                  title={`${screen.leagueMatch.matchDay}. ${translate(
                    "matchDay",
                    l
                  )} - ${showTranslated(screen.leagueMatch.league.name, l)}`}
                  leagueId={screen.leagueMatch.league.id}
                  matchDayOverride={screen.leagueMatch.matchDay}
                />
              </div>
            </div>
          </MainColumn>
          <RightRail>
            <RightPanel
              player1={screen.topScorer.topScorer1}
              player2={screen.topScorer.topScorer2}
              emblem1={screen.leagueMatch.team1.emblem}
              emblem2={screen.leagueMatch.team2.emblem}
              goals1={screen.topScorer.topScorer1Goals}
              goals2={screen.topScorer.topScorer2Goals}
            />
            <AdSlot
              type={AdSensePositionEnum.MATCH_RIGHT_RAIL}
              className="w-full"
              priority="adsense"
            />
            <MatchDayPanel
              title={`${screen.leagueMatch.matchDay}. ${translate(
                "matchDay",
                l
              )} - ${showTranslated(screen.leagueMatch.league.name, l)}`}
              leagueId={screen.leagueMatch.league.id}
              matchDayOverride={screen.leagueMatch.matchDay}
            />
          </RightRail>
        </TwoColumnGrid>
      </TabletContainer>
    </TabletPageDefault>
  );
};

export default connector(MatchWrapper);
